import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Grid, Avatar } from "antd";
import IntlMessage from "../util-components/IntlMessage";
import Icon from "../util-components/Icon";
import { connect, useDispatch, useSelector } from "react-redux";
import { SIDE_NAV_LIGHT, NAV_TYPE_SIDE } from "constants/ThemeConstant";
import utils from "utils";
import { onMobileNavToggle } from "redux/actions/Theme";
import { makeSelectCurrentUser } from "redux/selectors/Users";
import { APP_PREFIX_PATH } from "configs/AppConfig";
import { SettingOutlined } from "@ant-design/icons";
import translations from "configs/translations";
import AppLocale from "lang/index";
import { ROLE_MANDATORY_ORGANIZATION } from "constants/RoleConstants";
import unreadMsgInfoAdapter from "notification-adapter/unreadMsgInfoAdapter";
import { signOut } from "redux/actions/Auth";
import { useGetSquareConnectStatus } from "queries/payments";

const chosenLang = AppLocale.en.locale;

const { SubMenu } = Menu;
const { useBreakpoint } = Grid;

const setLocale = (isLocaleOn, localeKey) =>
  isLocaleOn ? <IntlMessage id={localeKey} /> : localeKey.toString();

const findSelectedKey = (config, pathname) => {
  let bestKey;
  let bestLength = -1;

  const visit = (items) => {
    items?.forEach((item) => {
      if (
        item.path &&
        (pathname === item.path || pathname.startsWith(`${item.path}/`)) &&
        item.path.length > bestLength
      ) {
        bestKey = item.key;
        bestLength = item.path.length;
      }
      if (item.submenu?.length) visit(item.submenu);
    });
  };

  visit(config);
  return bestKey;
};

const setDefaultOpen = (key) => {
  let keyList = [];
  let keyString = "";
  if (key) {
    const arr = key.split("-");
    for (let index = 0; index < arr.length; index++) {
      const elm = arr[index];
      index === 0 ? (keyString = elm) : (keyString = `${keyString}-${elm}`);
      keyList.push(keyString);
    }
  }
  return keyList;
};

const SideNavContent = (props) => {
  const {
    sideNavTheme,
    routeInfo,
    hideGroupTitle,
    localization,
    onMobileNavToggle,
    navigationConfig,
    navCollapsed,
  } = props;
  const isMobile = !utils.getBreakPoint(useBreakpoint()).includes("lg");
  const closeMobileNav = () => {
    if (isMobile) {
      onMobileNavToggle(false);
    }
  };

  const { pathname } = useLocation();
  const selectedMenuItem =
    findSelectedKey(navigationConfig, pathname) ?? pathname.split("/")[2];
  const user = useSelector(makeSelectCurrentUser());
  const [numberOfUnredMessages, setNumberOfUnredMessages] = useState(
    user.numb_of_unread_msg
  );
  const numbMsgRef = useRef();
  numbMsgRef.current = numberOfUnredMessages;
  const soundIsActive = useRef();
  soundIsActive.current = user?.new_msg_sound_is_active;
  const dispatch = useDispatch();

  const playNotificationSound = () => {
    const audio = new Audio("/sounds/notification.mp3");
    audio.play().catch((error) => console.error("Audio play failed:", error));
  };

  const handleNumbOfUnreadMsg = (value) => {
    if (
      value?.number_of_unread_msg > numbMsgRef.current &&
      soundIsActive.current
    ) {
      playNotificationSound();
    }
    setNumberOfUnredMessages(value?.number_of_unread_msg);
  };

  useEffect(() => {
    unreadMsgInfoAdapter.onUnreadMessageInfoReceived(handleNumbOfUnreadMsg);
  }, []);

  let navigationConfigForUser = [];

  navigationConfig.forEach((element) => {
    user?.app_access_list?.includes(element?.accessKey ?? element?.key) &&
      navigationConfigForUser.push(element);
  });

  const hasOrganization =
    !ROLE_MANDATORY_ORGANIZATION.includes(user?.role?.access_type) ||
    user?.organization;

  const hasPayments =
    user?.is_premium_user ||
    (user?.active_subscription &&
      user?.payment_information?.payment_method?.is_valid);

  const handleLogOut = () => {
    dispatch(signOut());
  };

  const userProfileMenu = {
    key: "user-profile",
    path: null,
    title:
      `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "User",
    breadcrumb: false,
    submenu: [
      {
        key: "edit-profile",
        path: `${APP_PREFIX_PATH}/edit-profile`,
        title: "Edit Profile",
      },
      {
        key: "change-password",
        path: `${APP_PREFIX_PATH}/edit-password`,
        title: "Change Password",
      },
      {
        key: "sign-out",
        title: "Sign Out",
        onClick: () => handleLogOut(),
      },
    ],
  };

  const baseNavigationConfig =
    hasOrganization && hasPayments
      ? navigationConfigForUser
      : [
          {
            key: "settings",
            path: `${APP_PREFIX_PATH}/settings`,
            title: translations[7][chosenLang],
            breadcrumb: false,
            submenu: [],
            icon: SettingOutlined,
          },
        ];

  const mainNavigationConfig = baseNavigationConfig;

  return (
    <div className="menu-container">
      <Menu
        theme={sideNavTheme === SIDE_NAV_LIGHT ? "light" : "dark"}
        mode="inline"
        style={{ height: "100%", borderRight: 0 }}
        defaultSelectedKeys={[routeInfo?.key]}
        defaultOpenKeys={setDefaultOpen(routeInfo?.key)}
        className={`${hideGroupTitle ? "hide-group-title" : ""} aeva-sidebar`}
        selectedKeys={[selectedMenuItem]}
      >
        {mainNavigationConfig.map((menu) =>
          menu.submenu.length > 0 ? (
            <SubMenu
              key={menu.key}
              title={
                <span>
                  <Icon type={menu?.icon} />
                  <span>{setLocale(localization, menu.title)}</span>
                </span>
              }
            >
              {menu.submenu.map((subMenuSecond) => (
                <Menu.Item
                  key={subMenuSecond.key}
                  onClick={
                    subMenuSecond.onClick
                      ? () => {
                          subMenuSecond.onClick();
                        }
                      : undefined
                  }
                >
                  <span>{setLocale(localization, subMenuSecond.title)}</span>
                  {subMenuSecond.key === "inbox" &&
                    numberOfUnredMessages > 0 && (
                      <span className="unread-mgs-info">
                        {numberOfUnredMessages}
                      </span>
                    )}
                  {subMenuSecond.icon ? (
                    <Icon type={subMenuSecond?.icon} />
                  ) : null}
                  {subMenuSecond.path && (
                    <Link
                      onClick={() => closeMobileNav()}
                      to={subMenuSecond.path}
                      disabled={
                        selectedMenuItem == "employee-calendar" &&
                        subMenuSecond.key == selectedMenuItem
                      }
                    />
                  )}
                </Menu.Item>
              ))}
            </SubMenu>
          ) : (
            <Menu.Item key={menu.key}>
              {menu.icon ? <Icon type={menu?.icon} /> : null}
              <span>{setLocale(localization, menu?.title)}</span>
              {menu.key == "conversations" && numberOfUnredMessages > 0 && (
                <span className="unread-mgs-info">{numberOfUnredMessages}</span>
              )}
              {menu.path ? (
                <Link
                  onClick={() => closeMobileNav()}
                  to={menu.path}
                  disabled={
                    selectedMenuItem === "scheduler" &&
                    menu.key === selectedMenuItem
                  }
                />
              ) : null}
            </Menu.Item>
          )
        )}
      </Menu>

      <Menu
        theme={sideNavTheme === SIDE_NAV_LIGHT ? "light" : "dark"}
        mode="inline"
        style={{ borderRight: 0 }}
        className="user-profile-menu"
        selectedKeys={[selectedMenuItem]}
      >
        <SubMenu
          key={userProfileMenu.key}
          title={
            <span>
              <Avatar
                size="small"
                src={user?.profile_picture?.thumbnail}
                style={{ marginRight: navCollapsed ? 0 : 8 }}
              >
                {!user?.profile_picture?.thumbnail &&
                  (user?.first_name?.[0] || "U")}
              </Avatar>
              {!navCollapsed && (
                <span>{setLocale(localization, userProfileMenu.title)}</span>
              )}
            </span>
          }
        >
          {userProfileMenu.submenu.map((subMenuSecond) => (
            <Menu.Item
              key={subMenuSecond.key}
              onClick={
                subMenuSecond.onClick
                  ? () => {
                      subMenuSecond.onClick();
                    }
                  : undefined
              }
            >
              <span>{setLocale(localization, subMenuSecond.title)}</span>
              {subMenuSecond.icon ? <Icon type={subMenuSecond?.icon} /> : null}
              {subMenuSecond.path && (
                <Link
                  onClick={() => closeMobileNav()}
                  to={subMenuSecond.path}
                  disabled={
                    selectedMenuItem == "employee-calendar" &&
                    subMenuSecond.key == selectedMenuItem
                  }
                />
              )}
            </Menu.Item>
          ))}
        </SubMenu>
      </Menu>
    </div>
  );
};

const TopNavContent = (props) => {
  const { topNavColor, localization, navigationConfig } = props;
  return (
    <Menu mode="horizontal" style={{ backgroundColor: topNavColor }}>
      {navigationConfig.map((menu) =>
        menu.submenu.length > 0 ? (
          <SubMenu
            key={menu.key}
            popupClassName="top-nav-menu aeva-sidebar"
            title={
              <span>
                {menu.icon ? <Icon type={menu?.icon} /> : null}
                <span>{setLocale(localization, menu.title)}</span>
              </span>
            }
          >
            {menu.submenu.map((subMenuFirst) =>
              subMenuFirst.submenu.length > 0 ? (
                <SubMenu
                  key={subMenuFirst.key}
                  icon={
                    subMenuFirst.icon ? (
                      <Icon type={subMenuFirst?.icon} />
                    ) : null
                  }
                  title={setLocale(localization, subMenuFirst.title)}
                >
                  {subMenuFirst.submenu.map((subMenuSecond) => (
                    <Menu.Item key={subMenuSecond.key}>
                      <span>
                        {setLocale(localization, subMenuSecond.title)}
                      </span>
                      <Link to={subMenuSecond.path} />
                    </Menu.Item>
                  ))}
                </SubMenu>
              ) : (
                <Menu.Item key={subMenuFirst.key}>
                  {subMenuFirst.icon ? (
                    <Icon type={subMenuFirst?.icon} />
                  ) : null}
                  <span>{setLocale(localization, subMenuFirst.title)}</span>
                  <Link to={subMenuFirst.path} />
                </Menu.Item>
              )
            )}
          </SubMenu>
        ) : (
          <Menu.Item key={menu.key}>
            {menu.icon ? <Icon type={menu?.icon} /> : null}
            <span>{setLocale(localization, menu?.title)}</span>
            {menu.path ? <Link to={menu.path} /> : null}
          </Menu.Item>
        )
      )}
    </Menu>
  );
};

// Sellable packages aren't supported for Square-connected organizations, so
// the Packages entry is stripped from the nav config (top-level and submenu).
const stripPackagesEntry = (config = []) =>
  config
    .filter((menu) => menu?.key !== "packages")
    .map((menu) =>
      menu?.submenu?.length
        ? {
            ...menu,
            submenu: menu.submenu.filter((sub) => sub?.key !== "packages"),
          }
        : menu
    );

const MenuContent = (props) => {
  const { connected: squareConnected, isLoading: squareStatusLoading } =
    useGetSquareConnectStatus();
  const hidePackages = squareConnected || squareStatusLoading;
  const navigationConfig = hidePackages
    ? stripPackagesEntry(props.navigationConfig)
    : props.navigationConfig;
  const contentProps = { ...props, navigationConfig };

  return props.type === NAV_TYPE_SIDE ? (
    <SideNavContent {...contentProps} />
  ) : (
    <TopNavContent {...contentProps} />
  );
};

const mapStateToProps = ({ theme }) => {
  const { sideNavTheme, topNavColor, navCollapsed } = theme;
  return { sideNavTheme, topNavColor, navCollapsed };
};

export default connect(mapStateToProps, { onMobileNavToggle })(MenuContent);
